<template>
  <div>
    <!-- Add Invoice Button -->
    <b-button 
      v-if="canAddInvoice" 
      id="show-btn" 
      size="sm" 
      variant="outline-primary" 
      @click="addInvoice">
      Add Invoice <i class="fa fa-plus" aria-hidden="true"></i>
    </b-button>

    <!-- Invoice Cards -->
    <b-card class="mt-2" v-for="(invoice, index) in invoiceList" :key="'invoice-' + index">
      <!-- Remove Invoice Button -->
      <div class="d-flex align-items-center" v-if="index != 0">
        <b-button 
          :disabled="isInvoiceRemovable(invoice)" 
          class="ml-auto" 
          id="show-btn" 
          size="sm"
          variant="danger" 
          @click="removeInvoice(index)">
          Remove Invoice <i class="fa fa-plus" aria-hidden="true"></i>
        </b-button>
      </div>

      <!-- Remove Single Invoice Button -->
      <RemoveSlineInvoiceButton 
        v-if="showRemoveSingleButton && moduleDetail.allModuleId && paymentDetail.paymentId"
        :BTNTitle="'Remove Single Invoice'" 
        :Type="'Single'" 
        :SlineType="config.slineType" 
        :TransportType="config.transportType"
        :SlinePayId="paymentDetail.paymentId"   
        :SlineInvId="invoice.invoiceId" 
        :allModuleId="moduleDetail.allModuleId" 
        @refresh-data="$emit('refresh-data')" />

      <!-- Revised Invoice Checkbox -->
      <div class="mt-2" v-if="showRevisedMailCheckbox">
        <input 
          :disabled="isRevisedInvoiceDisabled(invoice)"
          :id="'checkbox-' + index" 
          type="checkbox" 
          @click="toggleRevisedInvoice(index, 'checkbox-' + index)" />
        <span class="error">Revise Invoice</span>
      </div>

      <!-- Invoice Form -->
      <b-row align-v="start">
        <!-- Invoice Category -->
        <b-col :cols="getColumnSize('category')">
          <label>Invoice Category<span class="error">*</span></label>
          <b-form-radio-group 
            v-model="invoice.invCategory" 
            :options="config.invCategoryList" 
            class="mb-3"
            value-field="payVal" 
            text-field="payText" 
            disabled-field="notEnabled">
          </b-form-radio-group>
          <span class="error" v-if="invoice.invCategory_err">
            {{ invoice.invCategory_err }}
          </span>
        </b-col>

        <!-- Invoice Number -->
        <b-col :cols="getColumnSize('number')">
          <label>Invoice No<span class="error">*</span></label>
          <b-form-input 
            :formatter="capitalizeAll" 
            :disabled="isInvoiceDisabled(invoice)"
            name="invoiceNo" 
            v-model="invoice.invNo"
            @input="clearFieldError(invoice, 'invNo')">
          </b-form-input>
          <span class="error" v-if="invoice.invNo_err">
            {{ invoice.invNo_err }}
          </span>
        </b-col>

        <!-- Invoice Type -->
        <b-col :cols="getColumnSize('type')">
          <label>Invoice Type<span class="error">*</span></label>
          <CustomMultiSelect 
            :disabled="isInvoiceDisabled(invoice)"
            :options="config.invoiceTypeList" 
            :selected-options="invoice.invType" 
            placeholder="Select Type"
            v-model="invoice.invType" 
            @select="onInvoiceTypeSelect($event, invoice, index)" 
            :multiple="true"
            :error-class="getErrorClass(invoice, 'invType')">
          </CustomMultiSelect>
          <span class="error" v-if="invoice.invType_err">
            {{ invoice.invType_err }}
          </span>
        </b-col>
      </b-row>

      <!-- Invoice Date and File Upload -->
      <b-row align-v="start">
        <!-- Invoice Date -->
        <b-col cols="3">
          <label>Invoice Date<span class="error">*</span></label>
          <date-picker 
            :format="selectDateFormat" 
            lang="en" 
            name="inv_date" 
            id="inv_date"
            v-model="invoice.inv_date" 
            :minute-step="1"
            @input="clearFieldError(invoice, 'inv_date')" />
          <span class="error" v-if="invoice.inv_date_err">
            {{ invoice.inv_date_err }}
          </span>
        </b-col>

        <!-- File Upload -->
        <b-col cols="4">
          <label>Shipping Line Invoice (Drag & Drop)<span class="error">*</span></label>
          <multi-file-uploader 
            :disabled="isInvoiceDisabled(invoice) || invoice.enableDisable"
            :index="0" 
            :second_index="index"
            :style="'height: 79px; background: darkgray; width: 200px;'"
            :documentName="config.documentName" 
            :otherDocName="config.otherDocName"
            :folderName="'Sline_Invoice'" 
            :accept="'*'"
            :assign_no="getAssignmentNumber()" 
            :multiple="true"
            v-model="invoice.invSlineUpload" 
            :state="Boolean(invoice.invSlineUpload)"
            :fileAllModuleId="paymentDetail.allModuleId">
          </multi-file-uploader>
          <span class="error" v-if="invoice.invSlineUpload_err">
            {{ invoice.invSlineUpload_err }}
          </span>
          <a 
            v-if="invoice.invSlineUpload && invoice.invSlineUpload" 
            href="javascript:void(0)"
            @click="openSignedDocument(`ASSIGNMENT_UPLOADS/${getAssignmentNumber().replaceAll('/', '_')}/${invoice.invSlineUpload}`)">
            --View
          </a>
        </b-col>
      </b-row>

      <!-- Supplementary Comment Section -->
      <div class="row mt-2" v-if="invoice.invCategory == 'Supplementary'">
        <!-- Bill to Shipper -->
        <div class="col-md-4 row">
          <label class="col-md-4">Bill to Shipper : <span class="error">*</span></label>
          <div class="col-md-8">
            <b-form-radio-group 
              v-model="invoice.scCategory" 
              :options="config.scCategoryList" 
              class="mb-3"
              value-field="SCVal" 
              text-field="SCText" 
              @input="changeSCCategory(invoice, index)">
            </b-form-radio-group>
            <span class="error" v-if="showSCCategoryError(invoice)">
              {{ invoice.scCategory_err }}
            </span>
          </div>
        </div>

        <!-- Remark -->
        <div class="col-md-4 row" v-if="invoice.scCategory == 'Yes' || invoice.scCategory == 'No'">
          <label class="col-md-3">Remark : <span class="error">*</span></label>
          <div class="col-md-9">
            <b-form-textarea 
              :formatter="capitalizeAll" 
              id="remark" 
              name="remark"
              v-model="invoice.scremark">
            </b-form-textarea>
            <span class="error" v-if="showSCRemarkError(invoice)">
              Bill to Shipper Remark Field is Required
            </span>
          </div>
        </div>
      </div>

      <!-- Amount Details -->
      <b-card class="mt-2" v-for="(currency, currIndex) in invoice.invCurrencyList" :key="'currency-' + index + '-' + currIndex">
        <!-- Remove Amount Button -->
        <b-row v-if="currIndex != 0">
          <b-col>
            <div class="d-flex align-items-center mt-3">
              <b-button 
                class="ml-auto" 
                id="show-btn" 
                size="sm" 
                variant="outline-danger"
                @click="removeInvoiceCurrency(index, currIndex)">
                REMOVE AMOUNT
                <i class="fa fa-plus" aria-hidden="true"></i>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Amount Fields -->
        <div>
          <b-row>
            <!-- Invoice Amount -->
            <b-col cols="3">
              <label>Invoice Amount<span class="error">*</span></label>
              <b-form-input 
                :formatter="capitalizeAll" 
                :disabled="isAmountDisabled(invoice, currency)"
                name="invoiceAmount" 
                v-model="currency.invAmount"
                type="number"
                @input="clearCurrencyFieldError(currency, 'invAmount')">
              </b-form-input>
              <span class="error" v-if="currency.invAmount_err">
                {{ currency.invAmount_err }}
              </span>
            </b-col>

            <!-- Currency -->
            <b-col cols="3">
              <label>Currency<span class="error">*</span></label>
              <model-list-select 
                :isDisabled="isAmountDisabled(invoice, currency)"
                :list="config.currList" 
                v-model="currency.invCurrency" 
                option-value="payVal"
                option-text="payText" 
                placeholder="Select Currency"
                @input="clearCurrencyFieldError(currency, 'invCurrency')">
              </model-list-select>
              <span class="error" v-if="currency.invCurrency_err">
                {{ currency.invCurrency_err }}
              </span>
            </b-col>

            <!-- Payment Mode (for accounts users) -->
            <b-col v-if="showPaymentFields">
              <label>Payment Mode</label>
              <model-list-select 
                :isDisabled="isPaymentModeDisabled(currency)"
                :list="config.payList" 
                v-model="currency.invPayMode" 
                option-value="payVal" 
                option-text="payText"
                placeholder="Select Mode">
              </model-list-select>
              <span class="error" v-if="currency.invPayMode_err">
                {{ currency.invPayMode_err }}
              </span>
            </b-col>

            <!-- Payment Reference (for accounts users) -->
            <b-col v-if="showPaymentFields">
              <label>Payment Ref</label>
              <b-form-input 
                :disabled="isPaymentRefDisabled(currency)"
                :formatter="capitalizeAll" 
                name="paymentRef" 
                v-model="currency.invPayRef" 
                @input="changeDisableButton(currency.invPayRef)">
              </b-form-input>
              <span class="error" v-if="currency.invPayRef_err">
                {{ currency.invPayRef_err }}
              </span>
            </b-col>
          </b-row>

          <!-- Payment Details Row (for accounts users) -->
          <b-row v-if="showPaymentFields">
            <!-- Payment Amount -->
            <b-col cols="4">
              <label>Payment Amount</label>
              <b-form-input 
                :disabled="isPaymentAmountDisabled(currency)"
                :formatter="capitalizeAll" 
                name="paymentAmount"
                v-model="currency.invPaidAmount">
              </b-form-input>
              <span class="error" v-if="currency.invPaidAmount_err">
                {{ currency.invPaidAmount_err }}
              </span>
            </b-col>

            <!-- Payment Date -->
            <b-col class="mt-4" cols="4">
              <label>Payment Date</label>
              <date-picker 
                :disabled="isPaymentDateDisabled(currency)"
                :format="selectDateFormat" 
                lang="en" 
                name="paymentDate"
                v-model="currency.invPayDate" 
                :minute-step="1"
                @input="changeDisableButton(currency.invPayDate)">
              </date-picker>
              <span class="error" v-if="currency.invPayDate_err">
                {{ currency.invPayDate_err }}
              </span>
            </b-col>

            <!-- Payment Status -->
            <b-col cols="4">
              <label>Payment Status</label>
              <model-list-select 
                :isDisabled="isPaymentStatusDisabled(currency)"
                :list="config.statusList" 
                v-model="currency.invPayStatus" 
                option-value="payVal"
                option-text="payText" 
                placeholder="Select Status">
              </model-list-select>
              <span class="error" v-if="currency.invPayStatus_err">
                {{ currency.invPayStatus_err }}
              </span>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card>

    <!-- Save & Submit Button -->
    <md-layout md-gutter class="gutr" v-if="invoiceList.length > 0">
      <md-layout md-flex="25">
        <b-button 
          :disabled="isSaveDisabled"
          class="mt-3" 
          variant="primary" 
          id="addButtonLoaderSB" 
          @click="saveAndSubmit">
          Save & Submit
        </b-button>
        <md-spinner md-indeterminate id="loaderSB" style="display: none"></md-spinner>
      </md-layout>
    </md-layout>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import { ModelListSelect } from "vue-search-select";
import CustomMultiSelect from "./CustomMultiSelect.vue";
import MultiFileUploader from "./MultiSlineFileUploader.vue";
import RemoveSlineInvoiceButton from "./RemoveSlineInvoiceButton.vue";
import { DateFormat, selectDateFormat } from "../../helper/filters";
import { globalPermissionStatus } from "../../helper/collection";

export default {
  name: "InvoiceManagementComponent",
  components: {
    DatePicker,
    ModelListSelect,
    CustomMultiSelect,
    MultiFileUploader,
    RemoveSlineInvoiceButton,
  },
  props: {
    // Module specific configuration
    config: {
      type: Object,
      required: true,
      default: () => ({
        slineType: '', // 'CCSline', 'FFSline', 'Sline'
        transportType: '', // 'Export', 'Import'
        documentName: '',
        otherDocName: '',
        invCategoryList: [],
        invoiceTypeList: [],
        scCategoryList: [],
        currList: [],
        payList: [],
        statusList: [],
        columnSizes: {
          category: 3,
          number: 3,
          type: 6
        }
      })
    },
    // Module detail information
    moduleDetail: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // Payment detail information
    paymentDetail: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // Current invoice list
    invoiceList: {
      type: Array,
      required: true,
      default: () => []
    },
    // Whether to show revised mail checkbox
    showRevisedMailCheckbox: {
      type: Boolean,
      default: false
    },
    // Whether to show remove single button
    showRemoveSingleButton: {
      type: Boolean,
      default: true
    },
    // Whether save button is disabled
    saveDisabled: {
      type: Boolean,
      default: false
    },
    // Assignment number for file uploads
    assignmentNumber: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectDateFormat,
      revisedInvoiceDetails: [],
      items: [],
      lastSelectItem: {},
    };
  },
  computed: {
    canAddInvoice() {
      return !this.accounts || this.$sessionsStorage.get('nameUser') == 'Bhavneesh';
    },
    accounts() {
      return globalPermissionStatus("Accounts");
    },
    role() {
      return this.$sessionsStorage.get("designation");
    },
    showPaymentFields() {
      return (this.paymentDetail.paymentId && this.accounts) || 
             (this.paymentDetail.paymentId && this.role == 'Admin');
    },
    isSaveDisabled() {
      return this.paymentDetail.paymentDone == '1' || 
             this.saveDisabled ||
             this.disableSaveSubmit;
    },
    disableSaveSubmit() {
      return false; // Can be controlled by parent
    }
  },
  methods: {
    // Invoice Management
    addInvoice() {
      this.paymentDetail.paymentDone = "";
      let invCategory_val = "";
      
      if (this.paymentDetail.paymentId == "") {
        invCategory_val = "Normal";
      } else {
        invCategory_val = "Supplementary";
      }
      
      let newInvoice = {
        invoiceId: "",
        enableDisable: false,
        invId: "",
        allModuleId: "",
        invSlineUpload: "",
        invBlDraftUpload: "",
        invNo: "",
        inv_date: "",
        invAmount: "",
        invCurrency: "",
        invCurrencyList: [
          {
            invAmount: "",
            invCurrency: "",
            invPayMode: "",
            invPaidAmount: "",
            invPayRef: "",
            invPayDate: "",
            invPayStatus: "",
            invAmount_err: "",
            invCurrency_err: "",
            invPayMode_err: "",
            invPaidAmount_err: "",
            invPayRef_err: "",
            invPayDate_err: "",
            invPayStatus_err: "",
          },
        ],
        invType: [],
        invCategory: invCategory_val,
        scCategory: "",
        scremark: "",
        invTypeArr: [],
        invPayMode: "",
        invPaidAmount: "",
        invPayRef: "",
        invPayDate: "",
        invPayStatus: "",
        invCategory_err: "",
        scCategory_err: "",
        scremark_err: "",
        invSlineUpload_err: "",
        invType_err: "",
        invNo_err: "",
        inv_date_err: "",
      };
      
      this.$emit('add-invoice', newInvoice);
    },

    removeInvoice(index) {
      this.$emit('remove-invoice', index);
    },

    removeInvoiceCurrency(invIndex, currIndex) {
      this.$emit('remove-invoice-currency', invIndex, currIndex);
    },

    // Invoice Type Selection
    onInvoiceTypeSelect(items, invoice, index) {
      invoice.invType = items;
      this.clearFieldError(invoice, 'invType');
      this.$emit('invoice-type-changed', items, invoice, index);
    },

    // Revised Invoice Management
    toggleRevisedInvoice(index, id) {
      var checkBox = document.getElementById(id).checked;
      let invoice = this.invoiceList[index];
      
      if (checkBox) {
        this.disableSaveSubmit = false;
        invoice.enableDisable = false;
        invoice["Counter"] = index;
        this.revisedInvoiceDetails.push(invoice);
        invoice.invSlineUpload = "";
        invoice.invCurrencyList[0].invAmount = "";
      } else {
        this.revisedInvoiceDetails.splice(index, 1);
      }
      
      this.$emit('revised-invoice-changed', this.revisedInvoiceDetails);
    },

    // Supplementary Category Management
    changeSCCategory(invoice, index) {
      invoice.scCategory_err = "";
      invoice.scremark_err = "";
      this.$emit('sc-category-changed', invoice, index);
    },

    // Payment Management
    changeDisableButton(item) {
      if (item != undefined && item != null) {
        this.$emit('payment-field-changed', item);
      }
    },

    // Utility Methods
    capitalizeAll(value) {
      return value ? value.toUpperCase() : '';
    },

    getColumnSize(field) {
      return this.config.columnSizes[field] || 3;
    },

    getErrorClass(invoice, field) {
      return invoice[field + '_err'] ? 'is-invalid' : '';
    },

    getAssignmentNumber() {
      return this.assignmentNumber || this.moduleDetail.series || '';
    },



    // Validation Methods
    isInvoiceRemovable(invoice) {
      return invoice.invoiceId != '';
    },

    isInvoiceDisabled(invoice) {
      return (this.accounts && this.role != 'Admin') || invoice.invoiceId != '';
    },

    isRevisedInvoiceDisabled(invoice) {
      return invoice.invCurrencyList[0].invPayStatus == 2 || 
             invoice.invCurrencyList[0].invPayStatus == 1;
    },

    isAmountDisabled(invoice, currency) {
      return (this.accounts && this.role != 'Admin') || invoice.enableDisable;
    },

    isPaymentModeDisabled(currency) {
      return (!this.accounts && this.role != 'Admin') || currency.invPayStatus != '';
    },

    isPaymentRefDisabled(currency) {
      return !this.accounts || 
             (currency.invPayStatus != '' && 
              this.$sessionsStorage.get('cust_username') != 'laxmi@pridel.com' && 
              this.role != 'Admin');
    },

    isPaymentAmountDisabled(currency) {
      return (!this.accounts && this.role != 'Admin') || currency.invPayStatus != '';
    },

    isPaymentDateDisabled(currency) {
      return !this.accounts || 
             (currency.invPayStatus != '' && 
              this.$sessionsStorage.get('cust_username') != 'laxmi@pridel.com' && 
              this.role != 'Admin');
    },

    isPaymentStatusDisabled(currency) {
      return (!this.accounts && this.role != 'Admin') || currency.invPayStatus != '';
    },

    showSCCategoryError(invoice) {
      return (invoice.scCategory == '' || 
              invoice.scCategory == null || 
              invoice.scCategory == undefined) && 
             invoice.scCategory_err != '';
    },

    showSCRemarkError(invoice) {
      return invoice.scremark == '' || 
             invoice.scremark == null || 
             invoice.scremark == undefined;
    },

    // Validation Methods
    validateInvoice(invoice, index) {
      let isValid = true;
      
      // Clear previous errors
      this.clearInvoiceErrors(invoice);
      
      // Validate Invoice Category
      if (!invoice.invCategory) {
        invoice.invCategory_err = "Invoice Category is required";
        isValid = false;
      }
      
      // Validate Invoice Number
      if (!invoice.invNo || invoice.invNo.trim() === '') {
        invoice.invNo_err = "Invoice Number is required";
        isValid = false;
      }
      
      // Validate Invoice Type
      if (!invoice.invType || invoice.invType.length === 0) {
        invoice.invType_err = "Invoice Type is required";
        isValid = false;
      }
      
      // Validate Invoice Date
      if (!invoice.inv_date) {
        invoice.inv_date_err = "Invoice Date is required";
        isValid = false;
      }
      
      // Validate File Upload
      if (!invoice.invSlineUpload || invoice.invSlineUpload.trim() === '') {
        invoice.invSlineUpload_err = "Invoice file upload is required";
        isValid = false;
      }
      
      // Validate Supplementary Category if applicable
      if (invoice.invCategory === 'Supplementary') {
        if (!invoice.scCategory) {
          invoice.scCategory_err = "Bill to Shipper is required";
          isValid = false;
        }
        
        if ((invoice.scCategory === 'Yes' || invoice.scCategory === 'No') && 
            (!invoice.scremark || invoice.scremark.trim() === '')) {
          // Error will be shown by showSCRemarkError method
          isValid = false;
        }
      }
      
      // Validate Currency List
      if (!invoice.invCurrencyList || invoice.invCurrencyList.length === 0) {
        isValid = false;
      } else {
        invoice.invCurrencyList.forEach((currency, currIndex) => {
          this.validateCurrency(currency, currIndex);
          if (currency.invAmount_err || currency.invCurrency_err) {
            isValid = false;
          }
        });
      }
      
      return isValid;
    },
    
    validateCurrency(currency, currIndex) {
      // Clear previous errors
      this.clearCurrencyErrors(currency);
      
      // Validate Invoice Amount
      if (!currency.invAmount || currency.invAmount === '' || currency.invAmount <= 0) {
        currency.invAmount_err = "Invoice Amount is required and must be greater than 0";
      }
      
      // Validate Currency
      if (!currency.invCurrency) {
        currency.invCurrency_err = "Currency is required";
      }
      
      // Validate Payment fields if they are required
      if (this.showPaymentFields) {
        if (!currency.invPayMode) {
          currency.invPayMode_err = "Payment Mode is required";
        }
        
        if (!currency.invPayRef || currency.invPayRef.trim() === '') {
          currency.invPayRef_err = "Payment Reference is required";
        }
        
        if (!currency.invPaidAmount || currency.invPaidAmount === '' || currency.invPaidAmount <= 0) {
          currency.invPaidAmount_err = "Payment Amount is required and must be greater than 0";
        }
        
        if (!currency.invPayDate) {
          currency.invPayDate_err = "Payment Date is required";
        }
        
        if (!currency.invPayStatus) {
          currency.invPayStatus_err = "Payment Status is required";
        }
      }
    },
    
    clearInvoiceErrors(invoice) {
      invoice.invCategory_err = "";
      invoice.invNo_err = "";
      invoice.invType_err = "";
      invoice.inv_date_err = "";
      invoice.invSlineUpload_err = "";
      invoice.scCategory_err = "";
      invoice.scremark_err = "";
    },
    
    clearCurrencyErrors(currency) {
      currency.invAmount_err = "";
      currency.invCurrency_err = "";
      currency.invPayMode_err = "";
      currency.invPayRef_err = "";
      currency.invPaidAmount_err = "";
      currency.invPayDate_err = "";
      currency.invPayStatus_err = "";
    },
    
    clearFieldError(invoice, fieldName) {
      if (invoice[fieldName + '_err']) {
        invoice[fieldName + '_err'] = "";
      }
    },
    
    clearCurrencyFieldError(currency, fieldName) {
      if (currency[fieldName + '_err']) {
        currency[fieldName + '_err'] = "";
      }
    },
    
    validateAllInvoices() {
      let isValid = true;
      
      if (this.invoiceList.length === 0) {
        this.$toast.error("Please add at least one invoice");
        return false;
      }
      
      this.invoiceList.forEach((invoice, index) => {
        if (!this.validateInvoice(invoice, index)) {
          isValid = false;
        }
      });
      
      return isValid;
    },

    // Save and Submit
    saveAndSubmit() {
      if (this.validateAllInvoices()) {
        this.$emit('save-and-submit');
      } else {
        this.$toast.error("Please fix all validation errors before saving");
      }
    }
  }
};
</script>

<style scoped>
.error {
  color: red;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.ml-auto {
  margin-left: auto;
}

.d-flex {
  display: flex;
}

.align-items-center {
  align-items: center;
}

.row {
  margin-left: -15px;
  margin-right: -15px;
}

.col-md-3, .col-md-4 {
  padding-left: 15px;
  padding-right: 15px;
}

label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.btn {
  border-radius: 0.25rem;
}

.card {
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 0.25rem;
}

.card-body {
  padding: 1rem;
}
</style>



// WEBPACK FOOTER //
// InvoiceManagementComponent.vue?09da9096